/**
 * Las reglas del Frozen de Frutas: el puesto del carrito de la FIEC Vieja.
 *
 * Pasan frutas de derecha a izquierda, las rompes cayéndoles encima, y el puré llena el
 * vaso. Es el juego de
 * puntaje de un solo jugador, así que —al revés que el volley— no hay sala ni estado
 * sincronizado: el cliente juega, el servidor abre y cierra la partida y acredita.
 *
 * Aun así todo esto vive en shared, y por dos motivos concretos:
 *
 *   1. **`GAMES.frozen` se DERIVA de acá.** `maxScore` y `maxPointsPerSecond` no son
 *      números a ojo: salen de contar las frutas que el plan puede soltar y de cuánto
 *      vale la más cara. Si mañana pasan más rápido, el tope del servidor sube solo.
 *      Lo contrario —apretar el tope a mano cada vez que se toca el juego— es lo que
 *      termina rechazándole la partida a quien juega bien.
 *
 *   2. **El plan es una función pura de la semilla**, y la semilla la da el servidor
 *      (`POST /api/games/frozen/start`). Hoy sólo la usa el cliente para que la lluvia
 *      sea siempre la misma partida; el día que el puntaje se quiera validar de verdad,
 *      el servidor puede rehacer el plan y ver si lo que le reportan cabe en él. Esa
 *      puerta se cierra si el azar vive en el cliente.
 *
 * **Son constantes y funciones puras, sin estado.** `check:frozen` las ejercita sin
 * navegador ni servidor, y rehace las cuentas de los topes para que no se pudran.
 */
/**
 * El puesto tiene su PROPIO lienzo, como la cancha de volley y por lo mismo.
 *
 * El campus son 960x540 vistos desde arriba; esto se ve de perfil. El panel escala
 * este lienzo a lo que tenga de ancho.
 */
export const FIELD = { width: 640, height: 360 };
/**
 * El suelo del puesto. `y` crece hacia abajo, igual que en el campus y en Pixi.
 *
 * De acá en adelante las medidas llevan `FROZEN_` delante, y no es decoración: `shared`
 * reexporta todo PLANO desde su `index.ts`, así que el volley ya se quedó con
 * `GROUND_Y`, `JUMP_SPEED` y `PLAYER_SPEED`. Compartírselas sería peor que repetirlas —
 * ajustar el salto de un juego cambiaría el del otro, y son dos juegos distintos.
 */
export const FROZEN_GROUND_Y = 320;
/** Mismas medidas que el avatar del campus: es el mismo dibujo. */
export const FROZEN_PLAYER_WIDTH = 34;
export const FROZEN_PLAYER_HEIGHT = 52;
/** Unidades por segundo. Un pelo más rápido que el volley: acá hay que llegar. */
export const FROZEN_PLAYER_SPEED = 210;
/**
 * El salto, y de él sale toda la ventana de juego.
 *
 *   cenit = FROZEN_JUMP_SPEED² / (2 · FROZEN_GRAVITY) = 500² / 2500 = 100 unidades
 *
 * Con los pies a 320 y un cenit de 100, la banda donde se puede pisar algo va de y=220
 * a y=320. Las frutas pasan dentro de esa banda o el juego pide saltos que no existen:
 * de ahí salen `LANE_TOP` y `LANE_BOTTOM`, que la estrechan por los dos lados.
 *
 * Si tocas cualquiera de estos dos números, rehaz la cuenta — `check:frozen` la rehace.
 */
export const FROZEN_JUMP_SPEED = 500;
export const FROZEN_GRAVITY = 1250;
/**
 * El rebote al pisar una fruta, deliberadamente MENOR que el salto.
 *
 *   cenit del rebote = 380² / 2500 = 57,8 unidades
 *
 * Es lo que hace que encadenar sea una decisión y no una consecuencia: desde el rebote
 * sólo alcanzas lo que esté razonablemente cerca, así que la cadena la arma quien elige
 * bien la siguiente fruta, no quien salta una vez y aterriza de suerte sobre cuatro.
 */
export const BOUNCE_SPEED = 380;
/** Dura lo que dura una partida. No se acorta ni se alarga: ver `maxPointsPerSecond`. */
export const DURATION_SECONDS = 45;
/**
 * El ají te deja plantado. No te quita puntos ni tiempo, y eso es a propósito.
 *
 * Quitar puntos dejaría el puntaje pudiendo bajar, y entonces `maxScore` ya no sería
 * una cota de nada. Quitar TIEMPO es peor: acorta la partida, y como el servidor mide
 * el ritmo contra el tiempo transcurrido, una partida más corta con el mismo puntaje
 * se ve exactamente igual que un cliente modificado. El castigo es que te pierdes lo
 * que caiga mientras te sacudes el ardor, que además es el castigo que se entiende sin
 * leer nada.
 */
export const STUN_MS = 700;
/** Cuántos puntos de puré llenan un vaso. Es el marcador de sabor, no de puntaje. */
export const VASO_POINTS = 12;
/**
 * El hueco del vaso, en FRACCIONES de la caja del dibujo (`0..1`).
 *
 * Es la misma decisión que `ART_SHAPES` para la colisión de un mueble, y por el mismo
 * motivo: en fracciones, el nivel del puré sube con el puntaje sin reexportar el SVG, y
 * cambiar el vaso por otro dibujo es cambiar el archivo y estos cuatro números. En
 * píxeles sólo valdría para este vaso exacto.
 */
export const VASO_INNER = { left: 0.2, right: 0.8, top: 0.27, bottom: 0.94 };
/**
 * El surtido del carrito, que es el surtido de un carrito de Guayaquil.
 *
 * Lo caro es lo raro y lo raro es lo pequeño: el maracuyá vale el triple que un guineo,
 * sale la mitad de veces y es el blanco más chico de los cinco. Las tres cosas juntas
 * son lo que hace que valga la pena ir por él en vez de esperar a que caiga solo.
 */
export const FROZEN_KINDS = [
    { id: "guineo", name: "Guineo", points: 1, radius: 21, weight: 30, tint: 0xe0c063 },
    { id: "frutilla", name: "Frutilla", points: 1, radius: 19, weight: 24, tint: 0xc2564e },
    { id: "mora", name: "Mora", points: 2, radius: 17, weight: 18, tint: 0x6e5b86 },
    { id: "naranjilla", name: "Naranjilla", points: 2, radius: 18, weight: 14, tint: 0xd9884a },
    { id: "maracuya", name: "Maracuyá", points: 3, radius: 16, weight: 8, tint: 0xe8cd7a },
    { id: "aji", name: "Ají", points: 0, radius: 15, weight: 12, tint: 0xc0392b, hazard: true },
];
/** La más cara del surtido. De acá sale el tope de puntaje, así que se calcula. */
export const MAX_KIND_POINTS = FROZEN_KINDS.reduce((best, kind) => Math.max(best, kind.points), 0);
export function frozenKind(id) {
    return FROZEN_KINDS.find((kind) => kind.id === id);
}
// --------------------------------------------------------------------------
//  La cadena
// --------------------------------------------------------------------------
/**
 * Bono por encadenar sin tocar el suelo, y su tope.
 *
 * El bono es PLANO y no un multiplicador. Un multiplicador hace que el tope de puntaje
 * dependa de la cadena más larga imaginable, que no se puede acotar de verdad; un bono
 * plano con techo deja `maxScore` en una multiplicación que cabe en una línea y que
 * `check:frozen` puede rehacer. La cadena sigue siendo lo que más suma, que es lo que
 * importa para que se sienta bien.
 */
export const CHAIN_BONUS_MAX = 3;
/** Lo que vale la fruta `chain`-ésima de una cadena (`chain` empieza en 1). */
export function chainValue(basePoints, chain) {
    const bonus = Math.min(Math.max(chain - 1, 0), CHAIN_BONUS_MAX);
    return basePoints + bonus;
}
/** El máximo que puede valer una sola cosa que pasa. La cota de arriba del puntaje. */
export const MAX_POINTS_PER_FRUIT = chainValue(MAX_KIND_POINTS, CHAIN_BONUS_MAX + 1);
// --------------------------------------------------------------------------
//  El paso
// --------------------------------------------------------------------------
/**
 * Las frutas PASAN de derecha a izquierda; no caen.
 *
 * Es la diferencia con el juego del que sale esto, y cambia dónde está la dificultad:
 * cayendo, lo difícil es llegar a tiempo al sitio; pasando, lo difícil es el instante.
 * Toda fruta termina cruzando por delante de quien esté quieto, así que lo que decide
 * el punto no es dónde estás sino si vas bajando cuando te la cruzas.
 *
 * Lo que mantiene vivo el moverse son las ALTURAS. Un arco de salto baja una sola vez
 * por cada altura, así que dos frutas seguidas a distinta altura no caben en el mismo
 * salto: encadenarlas es ir a buscar la siguiente donde va a estar. Quien se queda
 * quieto saltando a ritmo rompe frutas; no encadena.
 */
/**
 * Velocidad del paso, del principio al final de la partida.
 *
 * El cruce son 720 unidades (`FRUIT_SPAWN_X` a `FRUIT_KILL_X`), así que al principio una
 * fruta tarda 6,5s en cruzar y al final 4,8s.
 *
 * Las dos quedan POR DEBAJO de `FROZEN_PLAYER_SPEED`, que son 210, y es deliberado: lo
 * que se te escapa siempre se puede ir a buscar. Un error cuesta tiempo —el que tardas
 * en darle alcance, que es tiempo en el que no estás rompiendo otra cosa— y no la fruta.
 *
 * El primer ajuste hacía lo contrario: 130 y 230, cruzando por encima del jugador a
 * mitad de partida para que el final no perdonara. Sobre el papel era mejor diseño y
 * jugándolo era peor — la fruta llegaba encima antes de dar tiempo a decidir nada, y un
 * fallo se volvía irrecuperable justo cuando más frutas había. La dificultad la pone la
 * CADENCIA; lo que sube acá es lo justo para que el final se note.
 */
export const PASS_SPEED_START = 110;
export const PASS_SPEED_END = 150;
/** Cadencia de aparición, del principio al final. Mismo criterio que la velocidad. */
export const SPAWN_START_MS = 900;
export const SPAWN_END_MS = 420;
/**
 * En cuántos segundos se llega a la cadencia y la velocidad finales.
 *
 * Menos que la partida a propósito: los últimos quince segundos van todos al máximo, y
 * eso le da un final a la partida. Una rampa que termina justo al acabarse el tiempo se
 * siente como que el juego se interrumpe, no como que termina.
 */
export const RAMP_SECONDS = 30;
/** Por dónde entra una fruta y dónde se la da por perdida. Las dos, fuera del lienzo. */
export const FRUIT_SPAWN_X = FIELD.width + 40;
export const FRUIT_KILL_X = -40;
/**
 * La franja de alturas por la que pasan. Son las alturas del CENTRO de la fruta.
 *
 *   pies de pie              320  (`FROZEN_GROUND_Y`)
 *   pies en el cenit         220  (320 menos las 100 que sube el salto)
 *   radio más grande          21  (el guineo)
 *
 * Del centro y no del borde, que fue el primer intento y salió mal: descontándole el
 * radio a cada fruta, al guineo le quedaban 18 unidades de franja y a la mora 26. Las
 * frutas grandes pasaban todas casi por la misma altura, y entonces la altura dejaba de
 * decidir nada — que es justo lo que le da sentido a moverse. Sobre el centro, las seis
 * tienen las mismas 36 unidades de variedad y lo que cambia es cuánta holgura les queda,
 * que es lo que `check:frozen` vigila por los dos extremos:
 *
 *   arriba   252 - 21 = 231, once unidades por debajo del cenit
 *   abajo    288 + 21 = 309, once unidades por encima del suelo
 *
 * Y las 36 caben en el rebote, que sube 58: desde una pisada en el borde de abajo se
 * llega al de arriba, así que la cadena puede recorrer la franja entera.
 */
export const LANE_TOP = 252;
export const LANE_BOTTOM = 288;
/** Interpolación lineal recortada a los extremos. */
function ramp(from, to, seconds) {
    const t = Math.min(Math.max(seconds / RAMP_SECONDS, 0), 1);
    return from + (to - from) * t;
}
export function passSpeedAt(seconds) {
    return ramp(PASS_SPEED_START, PASS_SPEED_END, seconds);
}
export function spawnIntervalAt(seconds) {
    return ramp(SPAWN_START_MS, SPAWN_END_MS, seconds);
}
/**
 * PRNG con semilla (mulberry32).
 *
 * Hace falta uno propio y no `Math.random` porque el plan tiene que ser reproducible a
 * partir del número que manda el servidor — que es lo único que hace que la partida sea
 * verificable más adelante. Treinta y dos bits alcanzan de sobra: la semilla del
 * servidor son 31 (`randomInt(0, 2**31 - 1)`).
 */
export function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Sorteo ponderado sobre el surtido. */
function pickKind(roll) {
    const total = FROZEN_KINDS.reduce((sum, kind) => sum + kind.weight, 0);
    let cursor = roll * total;
    for (const kind of FROZEN_KINDS) {
        cursor -= kind.weight;
        if (cursor <= 0)
            return kind;
    }
    // Sólo alcanzable por error de redondeo en el último tramo.
    return FROZEN_KINDS[FROZEN_KINDS.length - 1];
}
/**
 * El plan completo de una partida.
 *
 * Los TIEMPOS no dependen de la semilla —salen sólo de la rampa— y eso es lo que hace
 * que `maxFruits()` sea un número exacto y no una estimación: todas las partidas sueltan
 * la misma cantidad de cosas, y la semilla sólo decide cuáles y por qué altura. Es justo
 * lo que se necesita para acotar el puntaje sin acotar el juego.
 */
export function frozenPlan(seed) {
    const random = mulberry32(seed);
    const plan = [];
    let at = 0;
    while (true) {
        at += spawnIntervalAt(at) / 1000;
        if (at >= DURATION_SECONDS)
            break;
        const kind = pickKind(random());
        /* La franja es la del CENTRO, igual para todas: ver LANE_TOP. */
        const y = LANE_TOP + random() * (LANE_BOTTOM - LANE_TOP);
        plan.push({ at, y, kind: kind.id });
    }
    return plan;
}
/**
 * Cuántas cosas suelta una partida como máximo.
 *
 * Se cuenta recorriendo la misma rampa que usa `frozenPlan`, con una semilla cualquiera:
 * como los tiempos no dependen del azar, el resultado es el mismo para todas.
 */
export function maxFruits() {
    return frozenPlan(1).length;
}
/**
 * ¿Esta pisada cuenta?
 *
 * Tres condiciones, y ninguna sobra:
 *
 *   - **Vas cayendo** (`vy > 0`). Es la regla que eligió el juego: subir atravesando una
 *     fruta no la rompe. Sin esto, saltar en un montón las barre todas de una y el salto
 *     deja de tener puntería.
 *   - **El tramo que recorrieron los pies cruza la mitad de ARRIBA de la fruta.** Lo que
 *     la rompe es caerle encima, no rozarla de lado mientras bajas pegado a ella. Va
 *     contra el TRAMO y no contra la posición final por lo que explica `previousY`.
 *   - **Te solapas en horizontal**, con el ancho del cuerpo y no con un punto: los pies
 *     de un avatar de 34 de ancho no son una aguja.
 *
 * Es pura y sin estado para que `check:frozen` pueda ejercitar los bordes —justo encima,
 * justo debajo, subiendo, y el paso largo que antes se colaba— sin dibujar un fotograma.
 */
export function stomps(body, fruit) {
    if (body.vy <= 0)
        return false;
    const half = FROZEN_PLAYER_WIDTH / 2;
    if (Math.abs(body.x - fruit.x) > half + fruit.radius)
        return false;
    // El segmento [previousY, y] contra la banda [fruit.y - radius, fruit.y].
    return body.y >= fruit.y - fruit.radius && body.previousY <= fruit.y;
}
//# sourceMappingURL=frozen.js.map